(() => {
  'use strict';
  let configuration;

  // The configuration defines visible choices; Orbe validates available countries and languages.
  function choicesFor(countries, mappings = []) {
    const choices = [];
    const used = new Set();
    for (const option of mappings) {
      if (option.countryCode === null) {
        choices.push({ ...option, key: option.id || `global-${option.languageCode}` });
        continue;
      }
      const country = countries.find((item) => item.country === option.countryCode && !item.xteinkGlobalRoute);
      if (!country) continue;
      const locales = country.locales.split(',');
      const names = country.namelocales.split(',');
      const locale = locales.find((value) => value === option.languageCode) ||
        locales.find((value) => !option.languageCode.includes('-') && value.split('-')[0] === option.languageCode);
      if (!locale) continue;
      const key = `${country.country}-${locale}`;
      if (used.has(key)) continue;
      used.add(key);
      choices.push({ ...option, countryCode: country.country, countryName: option.countryName || country.countryname,
        languageCode: locale, languageName: option.languageName || names[locales.indexOf(locale)],
        region: option.region, key });
    }
    return choices;
  }

  function matches(choice, query) {
    const normalize = (value) => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase();
    return normalize(`${choice.countryName} ${choice.countryCode} ${choice.languageName} ${choice.languageCode}`).includes(normalize(query.trim()));
  }

  async function selectPair(modal, choice, { signal, timeout = 2000, countryNode } = {}) {
    if (signal?.aborted) throw new DOMException('Cancelled', 'AbortError');
    const country = countryNode || Array.from(modal.querySelectorAll('#orbeCountryList .md-form__select__country__list-link'))
      .find((element) => element.dataset.country === choice.countryCode && !element.dataset.xteinkGlobalRoute);
    if (!country || !country.dataset.locales.split(',').includes(choice.languageCode)) throw new Error('Country unavailable');
    // Invoke the plugin's existing handlers, including its connected-store metadata updates.
    country.click();
    const started = Date.now();
    let language;
    while (!signal?.aborted) {
      const selected = modal.querySelector('#md-form__select__country')?.dataset.value;
      language = Array.from(modal.querySelectorAll('#orbeLanguageList .md-form__select__language__list-link'))
        .find((element) => element.dataset.value === choice.languageCode);
      if (selected === choice.countryCode && language) break;
      if (Date.now() - started >= timeout) throw new Error('Language unavailable');
      await new Promise((resolve) => setTimeout(resolve, 25));
    }
    if (signal?.aborted) throw new DOMException('Cancelled', 'AbortError');
    language.click();
    if (modal.querySelector('#md-form__select__language')?.dataset.value !== choice.languageCode) throw new Error('Language unavailable');
  }

  function submitSelection(modal) {
    const submit = modal.querySelector('#md-btn__form__onSubmit');
    if (!submit || submit.disabled) throw new Error('Submission unavailable');
    submit.click(); // Use Orbe's preference and localization flow, without a second user click.
  }

  function recoverNative(doc, labels) {
    doc.defaultView?.XteinkCountryLanguageNavigation?.recoverSubmission?.();
    const modal = doc.getElementById('md-app-embed__modal');
    const title = modal?.querySelector('[data-mdapptitle]');
    if (title) title.textContent = labels.title;
    modal?.classList.add('country-language-native-fallback');
    const status = doc.querySelector('[data-country-language-status]');
    if (status) status.textContent = labels.errorText;
  }

  function currentChoiceFor(choices, country, language, global = false) {
    const fallback = choices.find((choice) => choice.countryCode === null);
    if (global || !choices.some((choice) => choice.countryCode === country)) return fallback;
    return choices.find((choice) => choice.countryCode === country && choice.languageCode === language);
  }

  async function recommendPair(modal, choices, { enabled = false, hasPreference = true, signal } = {}) {
    if (!enabled || hasPreference) return null;
    // Orbe already selected the IP country. Only normalize its proposed EU language;
    // never submit, arm navigation, or save a preference before the user confirms.
    const country = modal.querySelector('#md-form__select__country')?.dataset.value;
    const choice = choices.find((item) => item.region === 'europe' && item.countryCode === country);
    if (!choice) return null;
    await selectPair(modal, choice, { signal });
    return choice;
  }

  async function prepare(doc, labels, signal, { recommendation = false } = {}) {
    const modal = doc.getElementById('md-app-embed__modal');
    const form = modal?.querySelector('.md-modal__formContent');
    const title = modal?.querySelector('[data-mdapptitle]');
    const nativeCountries = Array.from(modal?.querySelectorAll('#orbeCountryList .md-form__select__country__list-link') || []);
    const footer = modal?.querySelector('.md-modal__footerContent');
    if (!form || !title || !footer || !nativeCountries.length) return null;
    let mappings = [];
    const navigation = doc.defaultView?.XteinkCountryLanguageNavigation;
    try {
      if (navigation) {
        configuration = await navigation.ready();
        navigation.bindRedirectPreparation();
        // Automatic opening means Orbe's IP result is now ready; refresh any early label fallback.
        if (recommendation) await navigation.refreshLabels();
      }
      if (!configuration) {
        const loading = new AbortController();
        const abort = () => loading.abort();
        const timer = setTimeout(abort, 2500);
        signal?.addEventListener('abort', abort, { once: true });
        try {
          const response = await fetch(labels.optionsUrl, { signal: loading.signal });
          if (!response.ok) throw new Error('Mapping unavailable');
          configuration = await response.json();
        } finally {
          clearTimeout(timer);
          signal?.removeEventListener('abort', abort);
        }
      }
      mappings = configuration.regions.flatMap((region) => region.options.map((option) => ({ ...option, region: region.id })));
    } catch (error) {
      if (signal?.aborted) return null;
      // Without the configuration, leave the native plugin controls available.
    }
    if (signal?.aborted || !doc.body.classList.contains('orbe--opened')) return null;
    const choices = choicesFor(nativeCountries.map((element) => element.dataset), mappings);
    if (!choices.length) return null;
    const hasSavedPreference = navigation?.hasPreference?.() ?? true;
    const recommended = await recommendPair(modal, choices, { enabled: recommendation,
      hasPreference: hasSavedPreference, signal });
    if (signal?.aborted || !doc.body.classList.contains('orbe--opened')) return null;
    const originalTitle = title.textContent;
    const selectionAbort = new AbortController();
    const view = doc.createElement('div');
    view.className = 'country-language-view';
    view.dataset.opening = recommendation ? 'automatic' : 'manual';
    view.dataset.preference = hasSavedPreference ? 'saved' : 'none';
    if (recommended) {
      view.dataset.recommendedCountry = recommended.countryCode;
      view.dataset.recommendedLanguage = recommended.languageCode;
    }
    const searchLabel = doc.createElement('label');
    searchLabel.className = 'country-language-view__search';
    const searchIcon = doc.querySelector('[data-country-language-search-icon]');
    if (searchIcon) {
      const icon = doc.createElement('span');
      icon.setAttribute('aria-hidden', 'true');
      icon.className = 'country-language-view__search-icon';
      icon.append(searchIcon.content.cloneNode(true));
      searchLabel.append(icon);
    }
    const search = doc.createElement('input');
    search.type = 'search';
    search.placeholder = labels.search;
    search.setAttribute('aria-label', labels.search);
    searchLabel.append(search);
    view.append(searchLabel);
    const results = doc.createElement('div');
    results.className = 'country-language-view__grid country-language-view__results';
    results.hidden = true;
    const empty = doc.createElement('p');
    empty.className = 'country-language-view__empty';
    empty.textContent = labels.empty;
    empty.hidden = true;
    view.append(results, empty);
    const buttons = new Set();
    let resultButtons = [];
    let selectedKey;
    let switching = false;
    let submitTimer;
    let destroyed = false;
    const createChoiceButton = (choice) => {
      const button = doc.createElement('button');
      button.type = 'button';
      button.className = 'country-language-view__choice';
      const name = doc.createElement('span');
      const language = doc.createElement('span');
      name.textContent = choice.countryName;
      language.textContent = choice.languageName;
      language.className = 'country-language-view__language';
      button.append(name, language);
      button.addEventListener('click', async () => {
        if (switching) return;
        switching = true;
        let submitted = false;
        buttons.forEach((item) => { item.disabled = true; });
        try {
          let nativeChoice = choice;
          let countryNode;
          if (choice.countryCode === null) {
            if (!navigation) throw new Error('Global navigation unavailable');
            const actual = navigation.globalCountry(modal);
            countryNode = actual.node;
            nativeChoice = { ...choice, countryCode: actual.countryCode };
            choice.actualCountry = actual.countryCode;
          } else {
            navigation?.removeVirtualRoute();
          }
          await selectPair(modal, nativeChoice, { signal: selectionAbort.signal, countryNode });
          if (selectionAbort.signal.aborted) throw new DOMException('Cancelled', 'AbortError');
          title.textContent = labels.title;
          selected.textContent = `${choice.countryName} / ${choice.languageName}`;
          selectedKey = choice.key;
          buttons.forEach((item) => item.setAttribute('aria-pressed', String(item.dataset.choice === choice.key)));
          navigation?.arm(choice);
          submitSelection(modal);
          submitted = true;
          submitTimer = setTimeout(() => {
            if (!view.isConnected || selectionAbort.signal.aborted) return;
            destroy();
            recoverNative(doc, labels);
          }, 8000);
        } catch (error) {
          navigation?.cancel();
          if (error.name !== 'AbortError') {
            destroy(); // Restore the working native controls if the app's DOM changes.
            recoverNative(doc, labels);
          }
        } finally {
          if (!submitted) {
            switching = false;
            buttons.forEach((item) => { item.disabled = false; });
          }
        }
      });
      button.dataset.choice = choice.key;
      button.disabled = switching;
      button.setAttribute('aria-pressed', String(choice.key === selectedKey));
      buttons.add(button);
      return button;
    };
    const renderGroup = (label, options) => {
      if (!options.length) return;
      const section = doc.createElement('section');
      section.className = 'country-language-view__section';
      const heading = doc.createElement('h3');
      heading.textContent = label;
      const grid = doc.createElement('div');
      grid.className = 'country-language-view__grid';
      options.forEach((choice) => grid.append(createChoiceButton(choice)));
      section.append(heading, grid);
      view.append(section);
    };
    const regionLabels = { 'north-america': labels.northAmerica, europe: labels.europe,
      'asia-pacific': labels.asiaPacific, 'middle-east': labels.middleEast,
      'latin-america': labels.latinAmerica, global: labels.global };
    for (const region of configuration.regions) {
      renderGroup(regionLabels[region.id] || region.id, choices.filter((choice) => choice.region === region.id));
    }
    const selected = doc.createElement('p');
    selected.className = 'country-language-view__selected';
    selected.setAttribute('role', 'status');
    const currentCountry = modal.querySelector('#md-form__select__country')?.dataset.value;
    const currentLanguage = modal.querySelector('#md-form__select__language')?.dataset.value;
    selected.textContent = [modal.querySelector('#md-form__select__country .md-form__select__span')?.textContent,
      modal.querySelector('#md-form__select__language .md-form__select__span')?.textContent].filter(Boolean).join(' / ');
    const current = currentChoiceFor(choices, currentCountry, currentLanguage, navigation?.isGlobal());
    if (current) {
      selectedKey = current.key;
      selected.textContent = `${current.countryName} / ${current.languageName}`;
      buttons.forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.choice === current.key)));
    }
    footer.prepend(selected);
    search.addEventListener('input', () => {
      resultButtons.forEach((button) => buttons.delete(button));
      // Show matching copies first, keeping the complete region lists in their original order.
      const query = search.value.trim();
      resultButtons = query ? choices.filter((choice) => matches(choice, query)).map(createChoiceButton) : [];
      results.replaceChildren(...resultButtons);
      results.hidden = !resultButtons.length;
      empty.hidden = !query || resultButtons.length > 0;
      view.scrollTop = 0;
    });
    form.before(view);
    modal.classList.remove('country-language-native-fallback');
    title.textContent = labels.title;
    modal.classList.add('country-language-grid-open');
    const onKeyDown = (event) => {
      if (event.key === 'Enter' && event.target === search) { event.preventDefault(); return; }
      if (!['Tab', 'Escape'].includes(event.key)) return;
      event.stopImmediatePropagation();
      if (event.key === 'Escape') {
        event.preventDefault();
        modal.querySelector('.md-modal-closeButtonAction, .md-modal-closeButton')?.click();
        return;
      }
      const focusable = Array.from(modal.querySelectorAll('button, input, a[href], [tabindex="0"]'))
        .filter((element) => !element.disabled && element.getClientRects().length);
      const next = event.shiftKey ? focusable.at(-1) : focusable[0];
      if (doc.activeElement === (event.shiftKey ? focusable[0] : focusable.at(-1))) {
        event.preventDefault();
        next?.focus();
      }
    };
    modal.addEventListener('keydown', onKeyDown, true);
    search.focus({ preventScroll: true });
    // Orbe focuses its dialog after its opening animation. Keep the search usable after that focus move.
    const focusTimer = setTimeout(() => {
      if (view.isConnected && doc.activeElement === modal.querySelector('.md-modal__dialog')) search.focus({ preventScroll: true });
    }, 550);
    function destroy() {
      if (destroyed) return;
      destroyed = true;
      clearTimeout(focusTimer);
      clearTimeout(submitTimer);
      signal?.removeEventListener('abort', destroy);
      selectionAbort.abort();
      view.remove();
      selected.remove();
      title.textContent = originalTitle;
      modal.classList.remove('country-language-grid-open');
      modal.removeEventListener('keydown', onKeyDown, true);
      navigation?.cancel();
    }
    signal?.addEventListener('abort', destroy, { once: true });
    return { destroy };
  }
  const api = { choicesFor, matches, selectPair, submitSelection, currentChoiceFor, recommendPair, prepare, recoverNative };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (typeof window !== 'undefined') window.XteinkCountryLanguageView = api;
})();
